import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  TextField, 
  InputAdornment, 
  IconButton, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid,
  Paper,
  Collapse,
  Button,
  Tooltip
} from '@mui/material';
// Remove date picker imports that are causing errors
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import CreateIcon from '@mui/icons-material/Create';
import axios from 'axios';
import FeedPost from './FeedPost';
import ComposePost from './ComposePost';
import { useAuth } from '../AuthContext';

// Add this constant for API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// This could match the groups used in ComposePost
const GROUPS = [
  { id: '', name: 'All Groups' },
  { id: 'Development', name: 'Development' },
  { id: 'Operations', name: 'Operations' },
  { id: 'Support', name: 'Support' },
  { id: 'Management', name: 'Management' },
  { id: 'Marketing', name: 'Marketing' },
];

const FeedContainer = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getToken } = useAuth();
  
  // Search and filter state
  const [searchText, setSearchText] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  // Replace DatePicker with string-based date filter
  const [dateFilter, setDateFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Cache for user display names to avoid redundant API calls
  const [userDisplayNames, setUserDisplayNames] = useState({});

  // New state for compose expansion
  const [isComposeExpanded, setIsComposeExpanded] = useState(false);

  // Add ref for the filters section
  const filterSectionRef = useRef(null);
  
  // Add tracking for open select/dropdown
  const [isAnyDropdownOpen, setIsAnyDropdownOpen] = useState(false);

  // Fetch all posts when component mounts
  useEffect(() => {
    fetchPosts();
  }, []);
  
  // Apply filters when posts or filter criteria change
  useEffect(() => {
    filterPosts();
  }, [posts, searchText, authorFilter, groupFilter, dateFilter]);

  // Handle clicks outside the filter section
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Skip if filters aren't showing, ref isn't set, or any dropdown is open
      if (!showFilters || !filterSectionRef.current || isAnyDropdownOpen) {
        return;
      }
      
      // Check if the click was within the filter section OR on the filter toggle button
      let isClickOnFilterButton = false;
      let target = event.target;
      
      // Traverse up the DOM tree to check for filter toggle button or MUI dialogs/dropdowns
      while (target) {
        // Skip if it's the filter toggle button
        if (target.dataset && target.dataset.filterToggle === 'true') {
          isClickOnFilterButton = true;
          break;
        }
        
        // Skip if it's a MUI dialog, popover, or select
        if (target.classList && (
            target.classList.contains('MuiPopover-root') || 
            target.classList.contains('MuiModal-root') || 
            target.classList.contains('MuiDialog-root') ||
            target.getAttribute('role') === 'presentation' ||
            target.getAttribute('aria-hidden') === 'true'
          )) {
          return; // Don't close if clicking on MUI overlay components
        }
        
        target = target.parentElement;
      }
      
      // Only close if the click was outside the section AND not on the toggle button
      // AND not on any MUI dialog/popover
      if (!filterSectionRef.current.contains(event.target) && !isClickOnFilterButton) {
        setShowFilters(false);
      }
    };
    
    // Add event listener if filters are showing
    if (showFilters) {
      // Use a small timeout to avoid capturing the same click that opened the filters
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilters, isAnyDropdownOpen]);

  // Function to fetch a user's display name from the API
  const fetchUserDisplayName = async (username) => {
    if (userDisplayNames[username]) {
      return userDisplayNames[username]; // Return from cache if available
    }
    
    try {
      const token = getToken();
      const apiUrl = process.env.REACT_APP_STATUS_API_URL || '';
      
      const response = await axios.get(
        `${apiUrl}/user`, 
        { 
          params: { username, token },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      
      let displayName = username; // Default to username
      
      if (response.data?.fornavn && response.data?.etternavn) {
        const firstName = response.data.fornavn.split(' ')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(' ');
        
        const lastName = response.data.etternavn.split(' ')
          .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(' ');
        
        displayName = `${firstName} ${lastName}`;
      }
      
      // Update cache
      setUserDisplayNames(prev => ({
        ...prev,
        [username]: displayName
      }));
      
      return displayName;
    } catch (error) {
      console.error(`Error fetching display name for ${username}:`, error);
      return username; // Fall back to username
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      // Update URL to use API_BASE_URL
      const response = await axios.get(`${API_BASE_URL}/feed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Process the posts to include display names
      const postsWithProcessedNames = await Promise.all(response.data.map(async post => {
        // Check if the post already has author_name
        if (!post.author_name && !post.display_name) {
          // If we have the username in our cache, use that
          if (userDisplayNames[post.created_by]) {
            post.author_name = userDisplayNames[post.created_by];
          } 
          // Check if backend returned display_name
          else if (post.display_name) {
            post.author_name = post.display_name;
          }
          // Otherwise, use the username as fallback
          else {
            post.author_name = post.created_by;
          }
        } else if (post.display_name) {
          // If post has display_name, use it as author_name
          post.author_name = post.display_name;
        }
        
        return post;
      }));
      
      // Sort posts by date in descending order (newest first)
      const sortedPosts = postsWithProcessedNames.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
      
      setPosts(sortedPosts);
      setFilteredPosts(sortedPosts); // Initialize filtered posts too
      setError(null);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load feed posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Parse search query to separate @ mentions and # hashtags from regular search
  const parseSearchQuery = (query) => {
    if (!query) return { textQuery: '', authorQuery: '', hashtagQuery: [] };
    
    // Match @username patterns
    const authorMatches = query.match(/@(\w+)/g); 
    // Match #hashtag patterns - improved to handle hashtags at word boundaries
    const hashtagMatches = query.match(/#(\w+)/g);
    
    let authorQuery = '';
    let hashtagQuery = [];
    let textQuery = query;
    
    if (authorMatches) {
      // Extract author name without @ symbol
      authorQuery = authorMatches.map(match => match.substring(1)).join(' ');
      // Remove author mentions from text query
      textQuery = textQuery.replace(/@\w+/g, '').trim();
    }
    
    if (hashtagMatches) {
      // Extract hashtags without # symbol and convert to lowercase
      hashtagQuery = hashtagMatches.map(match => match.substring(1).toLowerCase());
      // Remove hashtags from text query
      textQuery = textQuery.replace(/#\w+/g, '').trim();
    }
    
    console.log('Search parsed:', { textQuery, authorQuery, hashtagQuery });
    return { textQuery, authorQuery, hashtagQuery };
  };

  // Debug log for hashtag filtering
  const logPostHashtags = (posts) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Posts with hashtags:', posts.map(post => ({
        id: post.id,
        title: post.title,
        hashtags: post.hashtags || []
      })));
    }
  };

  // Enhanced debugging function for hashtag issues
  const debugHashtagMatch = (searchTag, postTags) => {
    console.log(`Comparing search tag "${searchTag}" against post tags:`, postTags);
    const matches = postTags.filter(tag => tag === searchTag);
    console.log(`Exact matches:`, matches);
    return matches.length > 0;
  };

  // Modify filterPosts to fix hashtag search issues
  const filterPosts = () => {
    let filtered = [...posts];
    
    // Parse search query for @ mentions and # hashtags
    const { textQuery, authorQuery, hashtagQuery } = parseSearchQuery(searchText);
    
    // Enhanced debug for hashtag search
    if (hashtagQuery.length > 0) {
      console.log('Active posts before hashtag filtering:', filtered.length);
      console.log('Search hashtags:', hashtagQuery);
      
      // Debug all posts' hashtags to see what we're working with
      filtered.forEach(post => {
        console.log(`Post ID ${post.id || 'unknown'} hashtags:`, post.hashtags || []);
      });
    }
    
    // Filter by parsed text search (content or title)
    if (textQuery) {
      const searchLower = textQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title?.toLowerCase().includes(searchLower) || 
        post.content?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by author from @ syntax or from author filter field
    const effectiveAuthorFilter = authorQuery || authorFilter;
    if (effectiveAuthorFilter.trim()) {
      const authorLower = effectiveAuthorFilter.toLowerCase();
      filtered = filtered.filter(post => {
        // Search in both display_name and created_by fields
        const displayName = post.display_name || post.author_name || '';
        const username = post.created_by || '';
        
        return displayName.toLowerCase().includes(authorLower) || 
               username.toLowerCase().includes(authorLower);
      });
    }
    
    // Fixed hashtag filtering - use exact matching
    if (hashtagQuery.length > 0) {
      filtered = filtered.filter(post => {
        // Safely get hashtags, ensuring it's an array
        const postTags = Array.isArray(post.hashtags) ? post.hashtags : [];
        
        // For empty hashtags, try extracting from content as fallback
        if (postTags.length === 0 && post.content) {
          const extractedTags = extractHashtags(post.content);
          if (extractedTags.length > 0) {
            console.log(`Extracted hashtags from post ${post.id} content:`, extractedTags);
            // For debugging only, don't modify the post object
            return hashtagQuery.every(searchTag => 
              extractedTags.some(tag => tag.toLowerCase() === searchTag.toLowerCase())
            );
          }
          return false;
        }
        
        // Convert all post tags to lowercase strings for comparison
        const normalizedPostTags = postTags.map(tag => 
          typeof tag === 'string' ? tag.toLowerCase() : String(tag).toLowerCase()
        );
        
        // Use EXACT matching (equality) instead of includes
        const hasAllTags = hashtagQuery.every(searchTag => {
          const searchTagLower = searchTag.toLowerCase();
          return normalizedPostTags.some(postTag => postTag === searchTagLower);
        });
        
        if (hasAllTags) {
          console.log(`Post ${post.id} matched all hashtags:`, hashtagQuery);
        }
        
        return hasAllTags;
      });
      
      console.log(`After hashtag filtering: ${filtered.length} posts match`);
    }
    
    // Filter by group
    if (groupFilter) {
      filtered = filtered.filter(post => post.group_id === groupFilter);
    }
    
    // Filter by date string (using includes for partial matches)
    if (dateFilter.trim()) {
      filtered = filtered.filter(post => {
        if (!post.date) return false;
        const postDateStr = new Date(post.date).toLocaleDateString();
        return postDateStr.includes(dateFilter);
      });
    }
    
    // Make sure we maintain the sort order (newest first)
    setFilteredPosts(filtered);
  };

  // Function to extract hashtags from content (copied from ComposePost for consistency)
  const extractHashtags = (text) => {
    if (!text) return [];
    
    const hashtagRegex = /#(\w+)/g;
    const matches = text.match(hashtagRegex);
    
    if (!matches) return [];
    
    return matches.map(tag => tag.substring(1).toLowerCase());
  };

  const clearFilters = () => {
    setSearchText('');
    setAuthorFilter('');
    setGroupFilter('');
    setDateFilter('');
  };

  const handlePostCreated = (newPost) => {
    // Make sure the post has author_name before adding to the list
    const postWithAuthor = {
      ...newPost,
      author_name: newPost.author_name || newPost.created_by
    };
    
    // Add the new post to the beginning of the list
    const updatedPosts = [postWithAuthor, ...posts];
    setPosts(updatedPosts);
    
    // Also update filtered posts if needed
    if (shouldIncludeInFilter(postWithAuthor)) {
      setFilteredPosts([postWithAuthor, ...filteredPosts]);
    }
  };
  
  // Helper function to check if post matches current filters
  const shouldIncludeInFilter = (post) => {
    // Check if the post matches all current filters
    if (searchText && !post.title?.toLowerCase().includes(searchText.toLowerCase()) && 
        !post.content?.toLowerCase().includes(searchText.toLowerCase())) {
      return false;
    }
    
    if (authorFilter && !post.created_by?.toLowerCase().includes(authorFilter.toLowerCase())) {
      return false;
    }
    
    if (groupFilter && post.group_id !== groupFilter) {
      return false;
    }
    
    if (dateFilter.trim()) {
      const postDateStr = new Date(post.date).toLocaleDateString();
      if (!postDateStr.includes(dateFilter)) {
        return false;
      }
    }
    
    return true;
  };

  const handleDeletePost = async (postId) => {
    try {
      const token = await getToken();
      // Update URL to use API_BASE_URL
      await axios.delete(`${API_BASE_URL}/feed/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(posts.filter(post => post.id !== postId));
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post. Please try again.');
    }
  };

  // Function to handle hashtag click - simplified to ensure it works reliably
  const handleHashtagClick = (hashtag) => {
    // Set the search to the exact hashtag - most reliable approach
    setSearchText(`#${hashtag}`);
    
    // Auto-open filters if they're closed
    if (!showFilters) {
      setShowFilters(true);
    }
    
    // Scroll to top to see search results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Toggle filter visibility
  const toggleFilters = (e) => {
    // When called directly from the button, don't let the event bubble
    // to avoid triggering the outside click handler right away
    if (e) {
      e.stopPropagation();
    }
    setShowFilters(!showFilters);
  };

  return (
    <Box sx={{ 
      p: 2, 
      height: '100%', 
      overflowY: 'auto',
      mr: '9px', // Add 5px margin to the right
      // Add custom scrollbar styling
      '&::-webkit-scrollbar': {
        width: '4px',
      },
      '&::-webkit-scrollbar-track': {
        background: theme => theme.palette.background.default || '#1c1e22',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(255, 255, 255, 0.25)',
      },
      // For Firefox
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(255, 255, 255, 0.15) #1c1e22',
    }}>
      {/* Search and filters */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: isComposeExpanded ? 3 : 1, // Adjust margin based on compose state
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box sx={{ 
          mb: showFilters ? 2 : 0,
          display: 'flex', 
          alignItems: 'center',
          gap: 1
        }}>
          {/* Search field with updated placeholder */}
          <TextField
            placeholder="Search posts... (use @name for author, #tag for hashtags)"
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {/* Mark the filter toggle button with a data attribute */}
                  <IconButton 
                    size="small" 
                    onClick={toggleFilters}
                    color={showFilters ? "primary" : "default"}
                    title="Advanced filters"
                    data-filter-toggle="true"
                  >
                    <TuneIcon />
                  </IconButton>
                  {searchText && (
                    <IconButton size="small" onClick={() => setSearchText('')} title="Clear search">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
          
          {/* New Compose button - visible when compose is collapsed */}
          {!isComposeExpanded && (
            <Tooltip title="Create new post">
              <Button
                variant="contained"
                color="primary"
                startIcon={<CreateIcon />}
                onClick={() => setIsComposeExpanded(true)}
                sx={{ 
                  minWidth: '120px',
                  height: '40px',
                  whiteSpace: 'nowrap'
                }}
              >
                New Post
              </Button>
            </Tooltip>
          )}
        </Box>
        
        {/* Filter section with ref */}
        <Collapse in={showFilters}>
          <Box ref={filterSectionRef}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField 
                  label="Author" 
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={authorFilter}
                  onChange={(e) => setAuthorFilter(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Group</InputLabel>
                  <Select
                    value={groupFilter}
                    label="Group"
                    onChange={(e) => setGroupFilter(e.target.value)}
                    // Add handlers to track dropdown open state
                    onOpen={() => setIsAnyDropdownOpen(true)}
                    onClose={() => setIsAnyDropdownOpen(false)}
                    MenuProps={{
                      // Keep menu in document for better positioning/styling
                      container: document.body
                    }}
                  >
                    {GROUPS.map(group => (
                      <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* Date input with enhanced click handling */}
                <TextField
                  label="Date (YYYY-MM-DD)"
                  placeholder="YYYY-MM-DD"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  onClick={(e) => e.stopPropagation()} // Prevent click from bubbling
                  onFocus={() => setIsAnyDropdownOpen(true)} // Treat focus as dropdown open
                  onBlur={() => setIsAnyDropdownOpen(false)} // Treat blur as dropdown close
                  InputProps={{
                    endAdornment: dateFilter && (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation(); // Stop propagation
                            setDateFilter('');
                          }}
                          edge="end"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation(); // Prevent from bubbling
                  clearFilters();
                }} 
                startIcon={<FilterAltIcon />}
                sx={{ mr: 1 }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>
      
      <ComposePost 
        onPostCreated={handlePostCreated}
        expanded={isComposeExpanded}
        onExpandedChange={setIsComposeExpanded}
      />
      
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : filteredPosts.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {filteredPosts.map((post) => (
            <FeedPost 
              key={post.id} 
              post={post} 
              onDelete={() => handleDeletePost(post.id)}
              onHashtagClick={handleHashtagClick}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            {posts.length > 0 
              ? 'No posts match your search criteria.' 
              : 'No posts yet. Be the first to share something!'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FeedContainer;
